import { api, http } from '../client';
import type { Address } from '@/types/domain';
import type {
  AddressType,
  DocumentStatus,
  DocumentType,
  IdentificationType,
  StudentAddress,
  StudentDocument,
  StudentIdentification,
  UUID,
} from '@/types/domain';

export interface IdentificationPayload {
  identificationType: IdentificationType;
  identificationNumber: string;
  issuingCountry?: string;
  issuedDate?: string;
  expiryDate?: string;
  primary: boolean;
}

export interface StudentAddressPayload {
  addressType: AddressType;
  address: Address;
  primary: boolean;
}

export interface DocumentMetadataPayload {
  documentType: DocumentType;
  title?: string;
  issuedDate?: string;
  expiryDate?: string;
}

export const studentProfileApi = {
  identifications: (studentId: UUID) =>
    api.get<StudentIdentification[]>(`/students/${studentId}/identifications`),
  addIdentification: (studentId: UUID, payload: IdentificationPayload) =>
    api.post<StudentIdentification>(`/students/${studentId}/identifications`, payload),
  updateIdentification: (studentId: UUID, id: UUID, payload: IdentificationPayload) =>
    api.put<StudentIdentification>(`/students/${studentId}/identifications/${id}`, payload),
  removeIdentification: (studentId: UUID, id: UUID) =>
    api.delete(`/students/${studentId}/identifications/${id}`),

  addresses: (studentId: UUID) => api.get<StudentAddress[]>(`/students/${studentId}/addresses`),
  addAddress: (studentId: UUID, payload: StudentAddressPayload) =>
    api.post<StudentAddress>(`/students/${studentId}/addresses`, payload),
  updateAddress: (studentId: UUID, id: UUID, payload: StudentAddressPayload) =>
    api.put<StudentAddress>(`/students/${studentId}/addresses/${id}`, payload),
  removeAddress: (studentId: UUID, id: UUID) =>
    api.delete(`/students/${studentId}/addresses/${id}`),
};

export const studentDocumentsApi = {
  list: (studentId: UUID, status?: DocumentStatus) =>
    api.get<StudentDocument[]>(`/students/${studentId}/documents`, {
      params: status !== undefined ? { status } : undefined,
    }),

  /**
   * Uploads the file and its metadata together.
   *
   * Sent as multipart with the metadata as a JSON part, because the endpoint
   * takes `@RequestPart`. The content type is set explicitly on that part —
   * without it the browser sends `text/plain` for a Blob and Spring refuses to
   * bind it.
   */
  upload: async (studentId: UUID, file: File, metadata: DocumentMetadataPayload) => {
    const form = new FormData();
    form.append('file', file);
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));

    const response = await http.post<{ data: StudentDocument }>(
      `/students/${studentId}/documents`,
      form,
      // Left undefined so the browser sets multipart/form-data with its boundary.
      { headers: { 'Content-Type': undefined } },
    );
    return response.data.data;
  },

  updateMetadata: (studentId: UUID, id: UUID, payload: DocumentMetadataPayload) =>
    api.put<StudentDocument>(`/students/${studentId}/documents/${id}`, payload),

  /** Records a registrar's verdict. A rejection must carry a reason. */
  verify: (studentId: UUID, id: UUID, approved: boolean, reason?: string) =>
    api.patch<StudentDocument>(`/students/${studentId}/documents/${id}/verification`, {
      approved,
      reason,
    }),

  remove: (studentId: UUID, id: UUID) => api.delete(`/students/${studentId}/documents/${id}`),

  /**
   * Fetches the file and hands the browser a save prompt.
   *
   * Routed through the API client rather than a plain link so the session's
   * bearer token is attached — the endpoint is authorised like any other, and a
   * bare href would arrive anonymous.
   */
  download: async (studentId: UUID, id: UUID, fileName: string) => {
    const response = await http.get<Blob>(`/students/${studentId}/documents/${id}/download`, {
      responseType: 'blob',
    });

    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    // Released immediately; the browser has already taken its own reference.
    URL.revokeObjectURL(url);
  },
};
